import { useState, useEffect, useCallback, useRef } from 'react';
import { getLastVisitedRoute, clearLastVisitedRoute } from '../../hooks/useLastVisitedRoute';

const DISPLAY_DURATION = 7000;
const FADE_DURATION = 400;

/**
 * Navigation comes from the shell, not the router — tabbed, this renders outside
 * every tab's router, so "Go" has to be routed into the active tab.
 *
 * @param activePathname  the path the shell considers current
 * @param onNavigate      called with the path to resume to
 */
export default function ResumeToast({ activePathname = '/', onNavigate }) {
  const [route, setRoute] = useState(null);
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);
  const timerRef = useRef(null);
  const pathname = activePathname;

  useEffect(() => {
    if (pathname !== '/') return;

    const saved = getLastVisitedRoute();
    if (!saved || saved.path === '/') return;

    setRoute(saved);
    setVisible(true);

    timerRef.current = setTimeout(() => {
      setFading(true);
      setTimeout(() => {
        setVisible(false);
        setFading(false);
      }, FADE_DURATION);
    }, DISPLAY_DURATION);

    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavigate = useCallback(() => {
    if (!route) return;
    clearTimeout(timerRef.current);
    setVisible(false);
    clearLastVisitedRoute();
    if (onNavigate) onNavigate(route.path);
  }, [route, onNavigate]);

  const handleDismiss = useCallback(() => {
    clearTimeout(timerRef.current);
    setFading(true);
    setTimeout(() => {
      setVisible(false);
      setFading(false);
    }, FADE_DURATION);
    clearLastVisitedRoute();
  }, []);

  if (!visible || !route) return null;

  return (
    <div
      style={{
        ...styles.container,
        opacity: fading ? 0 : 1,
        transform: fading ? 'translateY(12px)' : 'translateY(0)',
      }}
    >
      <div style={styles.content}>
        <div style={styles.icon}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#45B7AA"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 10 20 15 15 20" />
            <path d="M4 4v7a4 4 0 0 0 4 4h12" />
          </svg>
        </div>
        <div style={styles.textBlock}>
          <div style={styles.title}>Pick up where you left off</div>
          <div style={styles.label}>{route.label}</div>
        </div>
      </div>
      <div style={styles.actions}>
        <button onClick={handleDismiss} style={styles.dismiss}>
          Dismiss
        </button>
        <button onClick={handleNavigate} style={styles.go}>
          Go
        </button>
      </div>
      <div style={styles.progressTrack}>
        <div
          style={{
            ...styles.progressBar,
            animation: `resumeToastCountdown ${DISPLAY_DURATION}ms linear forwards`,
          }}
        />
      </div>
      <style>{`
        @keyframes resumeToastCountdown {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    position: 'fixed',
    bottom: 20,
    left: 20,
    right: 20,
    maxWidth: 380,
    margin: '0 auto',
    background: '#0f1219',
    border: '1px solid rgb(var(--accent-rgb) / 0.25)',
    borderRadius: 12,
    padding: '14px 16px 18px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    zIndex: 50000,
    boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
    fontFamily: "'Inter', 'Plus Jakarta Sans', sans-serif",
    transition: `opacity ${FADE_DURATION}ms ease, transform ${FADE_DURATION}ms ease`,
    overflow: 'hidden',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flex: 1,
    minWidth: 0,
  },
  icon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: 'rgb(var(--accent-rgb) / 0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  textBlock: {
    minWidth: 0,
  },
  title: {
    color: 'rgba(241,245,249,0.5)',
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
  },
  label: {
    color: '#f1f5f9',
    fontSize: 14,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  dismiss: {
    background: 'none',
    border: 'none',
    color: 'rgba(241,245,249,0.4)',
    fontSize: 12,
    cursor: 'pointer',
    padding: '6px 8px',
  },
  go: {
    background: 'var(--accent)',
    border: 'none',
    borderRadius: 8,
    color: '#06080f',
    fontSize: 13,
    fontWeight: 600,
    padding: '8px 20px',
    cursor: 'pointer',
  },
  progressTrack: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    background: 'rgb(var(--accent-rgb) / 0.1)',
    borderRadius: '0 0 12px 12px',
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    background: 'rgb(var(--accent-rgb) / 0.4)',
    borderRadius: '0 0 0 12px',
  },
};
